/**
 * Camera Controller - handles pan/zoom with keyboard and mouse
 */

import { Camera } from '@/rendering/Camera';
import { InputManager } from './InputManager';
import { EventBus } from '@/core/events/EventBus';

export class CameraController {
  private camera: Camera;
  private input: InputManager;

  private keyPanSpeed = 0.5;
  private frustumBase = 40;

  constructor(camera: Camera, input: InputManager) {
    this.camera = camera;
    this.input = input;

    // Mouse pan
    input.onPan = (dx: number, dy: number) => {
      const frustum = this.camera.getFrustumSize();
      const panX = -(dx / window.innerWidth) * frustum.width;
      const panZ = -(dy / window.innerHeight) * frustum.height;
      this.camera.pan(panX, panZ);
      EventBus.emit('camera:move', { x: this.camera.target.x, z: this.camera.target.z });
    };

    // Mouse zoom
    input.onZoom = (delta: number) => {
      const factor = Math.exp(delta * 0.12);
      this.camera.zoom *= factor;
      EventBus.emit('camera:zoom', { level: this.camera.zoom });
    };

    // Handle resize
    window.addEventListener('resize', this.onResize.bind(this));
  }

  private onResize(): void {
    this.camera.onResize();
  }

  update(deltaTime: number): void {
    // Keyboard pan
    let dx = 0;
    let dz = 0;

    if (this.input.isKeyDown('KeyW') || this.input.isKeyDown('ArrowUp')) {
      dz -= 1;
    }
    if (this.input.isKeyDown('KeyS') || this.input.isKeyDown('ArrowDown')) {
      dz += 1;
    }
    if (this.input.isKeyDown('KeyA') || this.input.isKeyDown('ArrowLeft')) {
      dx -= 1;
    }
    if (this.input.isKeyDown('KeyD') || this.input.isKeyDown('ArrowRight')) {
      dx += 1;
    }

    if (dx !== 0 || dz !== 0) {
      const speed = this.keyPanSpeed * this.frustumBase * this.camera.zoom * deltaTime;
      this.camera.pan(dx * speed, dz * speed);
      EventBus.emit('camera:move', { x: this.camera.target.x, z: this.camera.target.z });
    }
  }

  getWorldPosition(screenX: number, screenY: number): { x: number; z: number } {
    return this.camera.getWorldPosition(screenX, screenY);
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize.bind(this));
  }
}
